import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Alert, Anchor, Button, Collapse, Select, TextInput, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import {
  REQUEST_COLUMNS,
  REQUEST_MODES,
  can,
  computeRequestChecks,
  formatDay,
  formatInt,
  formatMoney,
  numOrNull,
  requestFieldTexts,
  requestInstructions,
  roleTitle,
  type CommitResponse,
  type PreviewResponse,
  type PreviewView,
  type RequestFormHeader,
  type RequestFormRow,
  type RequestItemsResponse,
  type RequestMode
} from '@parts/shared';
import { errorText, getJson, postJson } from '../../api';
import { DayInput } from '../../components/DayInput';
import { ModeTabs, PageHeader } from '../../components/layout/PageHeader';
import { FieldLabel, Metric } from '../../components/ui';
import { IconArrowDownLine, IconCircleCheck, IconEraser, IconHelp } from '../../icons';
import { EntryState, RequestFacts } from '../EntryParts';
import { InputGrid, type InputGridColumn, type InputGridHandle } from '../../components/table/InputGrid';
import { useI18n } from '../../i18n';
import { useRole } from '../../role';
import { useAppState } from '../../state';
import { PreviewModal } from '../purchase/PreviewModal';
import classes from '../purchase/PurchaseScreen.module.css';

const EMPTY: RequestFormHeader = { mode: 'new', reqNo: '', date: '', deadline: '', comment: '' };

/** Экран «Новый запрос»: отдел продаж заводит запрос клиента или правит существующий. */
export function RequestScreen() {
  const { lang, t, tx } = useI18n();
  const role = useRole();
  const { state, error: loadError, reload, apply } = useAppState();
  const [header, setHeader] = useState<RequestFormHeader>(EMPTY);
  const [showHelp, setShowHelp] = useState(false);
  const [preview, setPreview] = useState<{ view: PreviewView; version: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [posted, setPosted] = useState<{ no: string; message: string } | null>(null);
  const [tick, setTick] = useState(0);
  const grid = useRef<InputGridHandle | null>(null);

  const snap = state?.snapshot ?? null;
  const canPost = can(role, 'POST_REQUEST');

  useEffect(() => {
    setPreview(null);
  }, [snap?.version]);

  const rowsNow = useCallback((): RequestFormRow[] => (grid.current?.rows() ?? []) as RequestFormRow[], []);

  const checks = useMemo(() => {
    void tick;
    return computeRequestChecks(header, rowsNow());
  }, [header, tick, rowsNow]);

  /** Проверка всей таблицы сразу — для столбца «Проверка» (повтор OEM виден только между строками). */
  const checkRow = useCallback(
    (rows: Record<string, string>[]) => computeRequestChecks(header, rows as RequestFormRow[]).checks,
    [header]
  );

  const gridColumns = useMemo<InputGridColumn[]>(
    () =>
      REQUEST_COLUMNS.map((col) => ({
        key: col.key,
        title: tx(col.title),
        width: col.width,
        required: col.required,
        align: col.format === 'text' ? 'left' : 'right'
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang]
  );

  const loadRequest = useCallback(async (no: string, quiet = false) => {
    try {
      const res = await getJson<RequestItemsResponse>(`/api/requests/${encodeURIComponent(no)}`);
      setHeader((h) => ({ ...h, reqNo: res.no, deadline: res.deadline ?? '', comment: res.comment }));
      grid.current?.setRows(res.rows as Record<string, string>[]);
      if (!quiet) notifications.show({ color: 'accent', message: t('Загружено позиций: {n}', { n: res.rows.length }), autoClose: 3000 });
    } catch (err) {
      notifications.show({ color: 'red', message: tx(errorText(err)) });
    }
  }, []);

  const changeMode = (value: string) => {
    setHeader({ ...EMPTY, mode: value as RequestMode });
    grid.current?.setRows([]);
  };

  const changeRequest = (value: string | null) => {
    const no = value ?? '';
    setHeader((h) => ({ ...h, reqNo: no }));
    if (no) void loadRequest(no);
  };

  /** Ошибки в таблице: номера строк в уведомлении, таблица прокручивается к первой из них. */
  const showRowErrors = (count: number) => {
    const nums = grid.current?.errorRows() ?? [];
    notifications.show({
      color: 'red',
      title: t('Строк с ошибками: {n}', { n: count }),
      message: nums.length
        ? t('Строки {rows}. Причина — в столбце «Проверка» этих строк.', { rows: nums.join(', ') })
        : t('Причина — в столбце «Проверка».'),
      autoClose: 8000
    });
    grid.current?.showFirstError();
  };

  const openPreview = async () => {
    const rows = rowsNow();
    const res = computeRequestChecks(header, rows);
    if (res.errors > 0) {
      showRowErrors(res.errors);
      return;
    }
    setPreviewLoading(true);
    try {
      const answer = await postJson<PreviewResponse>('/api/requests/preview', { header, rows });
      if (answer.ok) setPreview({ view: answer.view, version: answer.version });
      else notifications.show({ color: 'red', title: t('Нельзя провести'), message: tx(answer.message) });
    } catch (err) {
      notifications.show({ color: 'red', title: t('Ошибка'), message: tx(errorText(err)) });
    } finally {
      setPreviewLoading(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const res = await postJson<CommitResponse>('/api/requests/commit', { header, rows: rowsNow(), version: preview.version });
      if (!res.ok) {
        notifications.show({ color: 'red', title: t('Не проведено'), message: tx(res.message) });
        if (res.message.includes('изменились')) {
          setPreview(null);
          void reload();
        }
        return;
      }
      setPreview(null);
      apply(res.state);
      const no = header.reqNo;
      notifications.show({ color: 'green', title: t('Запрос № {no} проведён', { no }), message: tx(res.message), autoClose: 8000 });
      setPosted({ no, message: res.message });
      setHeader({ ...EMPTY, mode: 'change', reqNo: no });
      await loadRequest(no, true);
    } catch (err) {
      notifications.show({ color: 'red', title: t('Ошибка'), message: tx(errorText(err)) });
    } finally {
      setCommitting(false);
    }
  };

  const header_ = (
    <PageHeader
      title={t('Новый запрос')}
      zoom
      subtitle={t('отдел продаж: запрос клиента или правки к проведённому')}
      tabs={<ModeTabs value={header.mode} onChange={changeMode} items={REQUEST_MODES.map((m) => ({ value: m.id, label: tx(m.title) }))} />}
    />
  );

  if (loadError || !snap || !state)
    return (
      <div className={classes.page}>
        {header_}
        <EntryState error={loadError} onRetry={() => void reload()} />
      </div>
    );

  const texts = requestFieldTexts(header.mode);
  const selected = snap.reqs.find((r) => r.no === header.reqNo) ?? null;
  const totals = rowsNow().reduce(
    (acc, r, i) => {
      if (!checks.checks[i]?.ok) return acc;
      const qty = numOrNull(r.qty) ?? 0;
      const price = numOrNull(r.price);
      if (qty <= 0) return acc;
      return { positions: acc.positions + 1, qty: acc.qty + qty, amount: acc.amount + (price !== null ? qty * price : 0) };
    },
    { positions: 0, qty: 0, amount: 0 }
  );

  return (
    <div className={classes.page}>
      {header_}

      <div className={classes.body}>
        {posted && (
          <Alert color="go" variant="light" withCloseButton onClose={() => setPosted(null)} title={t('Запрос № {no} проведён', { no: posted.no })}>
            {tx(posted.message)} {t('Он виден отделу закупки и на экране')}{' '}
            <Anchor component={Link} to="/requests">
              «{t('Текущие запросы')}»
            </Anchor>
            .
          </Alert>
        )}
        {!canPost && (
          <Alert color="yellow" variant="light" py="xs">
            {t('Роль «{role}» может заполнять форму, но не проводить запросы. Права выдаёт администратор.', { role: tx(roleTitle(role)) })}
          </Alert>
        )}

        <section className={classes.form}>
          <div className={classes.fields}>
            {header.mode === 'new' ? (
              <TextInput
                label={<FieldLabel text={tx(texts.reqNo.label)} hint={tx(texts.reqNo.hint)} />}
                withAsterisk
                value={header.reqNo}
                onChange={(e) => setHeader({ ...header, reqNo: e.target.value })}
              />
            ) : (
              <Select
                label={<FieldLabel text={tx(texts.reqNo.label)} hint={tx(texts.reqNo.hint)} />}
                placeholder={t('Выберите запрос')}
                withAsterisk
                searchable
                nothingFoundMessage={t('Не найдено')}
                value={header.reqNo || null}
                onChange={changeRequest}
                data={snap.reqs
                  .slice()
                  .reverse()
                  .map((r) => ({ value: r.no, label: r.no }))}
              />
            )}
            <DayInput
              label={<FieldLabel text={tx(texts.date.label)} hint={tx(texts.date.hint)} />}
              placeholder={t('Сегодня, {date}', { date: formatDay(snap.today) })}
              value={header.date}
              onChange={(v) => setHeader({ ...header, date: v })}
            />
            <TextInput
              label={<FieldLabel text={tx(texts.comment.label)} hint={tx(texts.comment.hint)} />}
              value={header.comment}
              onChange={(e) => setHeader({ ...header, comment: e.target.value })}
            />
          </div>

          <div className={classes.infoLine}>
            {selected && header.mode === 'change' ? (
              <RequestFacts req={selected} />
            ) : (
              <span className={classes.infoMuted}>
                {header.mode === 'new'
                  ? t('Вставьте таблицу клиента в таблицу ниже: OEM, наименование, количество, цена.')
                  : t('Выберите номер запроса — его состав загрузится в таблицу.')}
              </span>
            )}
          </div>
        </section>

        <div className={classes.toolbar}>
          <div className={classes.tools}>
            {header.mode === 'change' && (
              <Button
                variant="default"
                leftSection={<IconArrowDownLine size={15} />}
                onClick={() => header.reqNo && void loadRequest(header.reqNo)}
                disabled={!header.reqNo}
              >
                {t('Загрузить состав')}
              </Button>
            )}
            <Tooltip label={t('Очистить таблицу')}>
              <ActionIcon variant="subtle" color="gray" size={36} onClick={() => grid.current?.setRows([])} aria-label={t('Очистить таблицу')}>
                <IconEraser size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={showHelp ? t('Скрыть инструкцию') : t('Как заполнять')}>
              <ActionIcon
                variant={showHelp ? 'light' : 'subtle'}
                color={showHelp ? 'accent' : 'gray'}
                size={36}
                onClick={() => setShowHelp((v) => !v)}
                aria-label={t('Как заполнять')}
              >
                <IconHelp size={16} />
              </ActionIcon>
            </Tooltip>
          </div>
          <div className={classes.summary}>
            <div className={classes.metrics}>
              <Metric label={t('Позиций')} value={formatInt(totals.positions)} />
              <Metric label={t('Штук')} value={formatInt(totals.qty)} />
              <Metric label={t('Сумма')} value={formatMoney(totals.amount)} />
              <Metric label={t('С ошибками')} value={checks.errors} tone={checks.errors ? 'red' : undefined} />
            </div>
            <Button
              color="go"
              className={classes.postButton}
              leftSection={<IconCircleCheck size={17} />}
              onClick={() => void openPreview()}
              loading={previewLoading}
              disabled={!canPost}
            >
              {t('Проверить и провести')}
            </Button>
          </div>
        </div>

        <Collapse expanded={showHelp}>
          <ul className={classes.help}>
            {requestInstructions(header.mode).map((line) => (
              <li key={line}>{tx(line)}</li>
            ))}
            <li>{t('Таблица работает как в Excel: щелчок выделяет ячейку, двойной щелчок, Enter или начало набора — правка. Ctrl+C и Ctrl+V, выделение мышью, ширина столбцов запоминается.')}</li>
          </ul>
        </Collapse>

        <div className={classes.tableArea}>
          <InputGrid
            gridId={`request-${header.mode}`}
            columns={gridColumns}
            check={checkRow}
            handle={grid}
            onChange={() => setTick((t) => t + 1)}
          />
        </div>
      </div>

      <PreviewModal
        view={preview?.view ?? null}
        committing={committing}
        canCommit={canPost}
        onClose={() => setPreview(null)}
        onCommit={() => void commit()}
      />
    </div>
  );
}
